import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { ListVisitsResponse } from '@yui-receipt/shared'
import { api } from '@/lib/api'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

export default function VisitListPage() {
  const [from, setFrom] = useState<string>(todayIso())
  const [to, setTo] = useState<string>(todayIso())
  const [q, setQ] = useState('')

  const { data, isLoading, error, refetch, isFetching } = useQuery<ListVisitsResponse>({
    queryKey: ['visits', from, to, q],
    queryFn: () => {
      const params = new URLSearchParams()
      if (from) params.set('from', from)
      if (to) params.set('to', to)
      if (q.trim()) params.set('q', q.trim())
      params.set('limit', '100')
      params.set('withTotal', '1')
      return api<ListVisitsResponse>(`/visits?${params.toString()}`)
    },
  })

  const visits = data?.visits ?? []

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <section className="card">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h1 className="text-xl font-semibold">เข้ารับการรักษา</h1>
          <Link to="/visits/new" className="btn-primary min-h-tap">
            + เข้ารับการรักษาใหม่
          </Link>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="label">ตั้งแต่</label>
            <input
              className="input"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              max={todayIso()}
            />
          </div>
          <div>
            <label className="label">ถึง</label>
            <input
              className="input"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              max={todayIso()}
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="label">ค้นหาคนไข้</label>
            <input
              className="input"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ชื่อ / HN"
            />
          </div>
          <button className="btn-secondary min-h-tap" onClick={() => refetch()}>
            {isFetching ? 'กำลังโหลด…' : '🔄'}
          </button>
        </div>
      </section>

      <section className="card">
        {isLoading ? (
          <p className="text-slate-500 text-center py-8">กำลังโหลด…</p>
        ) : error ? (
          <p className="text-rose-600 text-center py-8">โหลดไม่สำเร็จ</p>
        ) : visits.length === 0 ? (
          <p className="text-slate-500 text-center py-8">ไม่พบการรักษาในช่วงนี้</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visits.map((v) => (
              <li key={v.id}>
                <Link
                  to={`/visits/${v.id}/record`}
                  className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 rounded-md gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {v.patientNameSnapshot}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      HN {v.patientHnSnapshot} • {v.visitDate}
                      {v.timeIn ? ` • ${v.timeIn}` : ''}
                    </div>
                  </div>
                  <span
                    className={
                      'text-xs px-2 py-0.5 rounded-full shrink-0 ' +
                      (v.visitType === 'initial'
                        ? 'bg-brand-50 text-brand-700'
                        : 'bg-slate-100 text-slate-600')
                    }
                  >
                    {v.visitType === 'initial' ? 'ครั้งแรก' : 'ติดตามผล'}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
